import * as React from "react"

interface UseScriptOptions {
  /** The URL of the script to load */
  src: string
  /** Whether to load the script asynchronously (default: true) */
  async?: boolean
  /** Whether to defer script execution (default: false) */
  defer?: boolean
  /** Custom id for the script tag (useful for preventing duplicate loads) */
  id?: string
  /** Element to append the script to. Can be a ref, HTMLElement, CSS selector, or 'head'/'body'. Defaults to 'head' */
  appendTo?: React.RefObject<HTMLElement> | HTMLElement | string | 'head' | 'body'
}

interface UseScriptReturn {
  /** Whether the script is currently loading */
  loading: boolean
  /** Whether the script has loaded successfully */
  ready: boolean
  /** Any error that occurred during loading */
  error: Error | null
}

/**
 * Hook to dynamically load a script from a URL
 * 
 * @param options - Configuration options for the script
 * @returns Object containing loading state, ready state, and error state
 * 
 * @example
 * ```tsx
 * // Append to document head (default)
 * const { loading, ready, error } = useScript({
 *   src: 'https://example.com/script.js',
 *   async: true,
 *   id: 'my-script'
 * });
 * 
 * // Append to a specific element using a ref
 * const containerRef = useRef<HTMLDivElement>(null);
 * useScript({
 *   src: 'https://example.com/script.js',
 *   appendTo: containerRef
 * });
 * 
 * // Append to a specific element using a selector
 * useScript({
 *   src: 'https://example.com/script.js',
 *   appendTo: '#my-container'
 * });
 * ```
 */
export function useScript(options: UseScriptOptions): UseScriptReturn {
  const { src, async = true, defer = false, id, appendTo = 'head' } = options

  const [loading, setLoading] = React.useState<boolean>(true)
  const [ready, setReady] = React.useState<boolean>(false)
  const [error, setError] = React.useState<Error | null>(null)

  // Use ref to track if script has loaded to avoid dependency issues
  const scriptLoadedRef = React.useRef<boolean>(false)

  React.useEffect(() => {
    // Reset state when src or appendTo changes
    setLoading(true)
    setReady(false)
    setError(null)
    scriptLoadedRef.current = false

    // Nothing to load yet (e.g. waiting for consent state) — stay in "loading" without touching the DOM
    if (!src) return
    
    // Helper function to get the target element
    const getTargetElement = (): HTMLElement | null => {
      if (!appendTo || appendTo === 'head') {
        return document.head
      }
      if (appendTo === 'body') {
        return document.body
      }
      if (typeof appendTo === 'string') {
        // CSS selector
        return document.querySelector(appendTo) as HTMLElement
      }
      if ('current' in appendTo) {
        // React ref
        return appendTo.current
      }
      // Direct HTMLElement
      return appendTo
    }
    
    // Function to load the script
    const loadScript = (targetElement: HTMLElement) => {
      // Check if script is already loaded
      const scriptId = id || `script-${src.replace(/[^a-zA-Z0-9]/g, '-')}`
      const existingScript = document.getElementById(scriptId) as HTMLScriptElement

      // If script already exists and is loaded, mark as ready
      if (existingScript) {
        if (existingScript.dataset.loaded === 'true') {
          setLoading(false)
          setReady(true)
          scriptLoadedRef.current = true
          return () => {}
        }
        // If script exists but not loaded yet, wait for it
        const handleLoad = () => {
          setLoading(false)
          setReady(true)
          scriptLoadedRef.current = true
        }
        const handleError = () => {
          setLoading(false)
          setError(new Error(`Failed to load script: ${src}`))
        }

        existingScript.addEventListener('load', handleLoad)
        existingScript.addEventListener('error', handleError)

        return () => {
          existingScript.removeEventListener('load', handleLoad)
          existingScript.removeEventListener('error', handleError)
        }
      }

      // Create and load new script
      const script = document.createElement('script')
      script.src = src
      script.async = async
      script.defer = defer
      script.id = scriptId

      const handleLoad = () => {
        script.dataset.loaded = 'true'
        scriptLoadedRef.current = true
        setLoading(false)
        setReady(true)
        setError(null)
      }

      const handleError = () => {
        setLoading(false)
        setError(new Error(`Failed to load script: ${src}`))
        // Remove script on error to allow retry
        if (script.parentNode) {
          script.parentNode.removeChild(script)
        }
      }

      script.addEventListener('load', handleLoad)
      script.addEventListener('error', handleError)

      // Append script to the target element
      targetElement.appendChild(script)

      // Return cleanup function
      return () => {
        script.removeEventListener('load', handleLoad)
        script.removeEventListener('error', handleError)
        
        // Only remove script if component unmounts before it loads
        // If it's already loaded, we keep it for other components to use
        if (!scriptLoadedRef.current && script.parentNode) {
          script.parentNode.removeChild(script)
        }
      }
    }
    
    // Get target element
    const targetElement = getTargetElement()
    
    // If target element doesn't exist yet (e.g., ref not attached), wait for it
    if (!targetElement) {
      // For refs, wait a bit for them to be attached
      if (appendTo && typeof appendTo === 'object' && 'current' in appendTo) {
        let cleanup: (() => void) | undefined
        
        const checkInterval = setInterval(() => {
          const element = getTargetElement()
          if (element) {
            clearInterval(checkInterval)
            cleanup = loadScript(element)
          }
        }, 50)
        
        // Cleanup after a reasonable timeout
        const timeout = setTimeout(() => {
          clearInterval(checkInterval)
          setLoading(false)
          setError(new Error(`Target element not found for script: ${src}`))
        }, 5000)
        
        return () => {
          clearInterval(checkInterval)
          clearTimeout(timeout)
          if (cleanup) cleanup()
        }
      } else {
        // For selectors or other cases, set error immediately
        setLoading(false)
        setError(new Error(`Target element not found for script: ${src}`))
        return
      }
    }
    
    // Load the script
    return loadScript(targetElement)
  }, [src, async, defer, id, appendTo])

  return { loading, ready, error }
}
